import React from 'react';
import {
  WithStyles,
  withStyles,
  createStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import SurveyItem from '../../util/SurveyItem';
import {
  withSurveyContext,
  withIsReadOnly,
  WithIsReadOnlyInjectedProps,
  WithSurveyContextInjectedProps,
} from '../Contexts';
import SurveyField from '../SurveyField';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';
import ChoiceOptions from '../../util/ChoiceOptions';
import { TableRow, TableCell } from '@mui/material';
import { FieldProps } from 'formik';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import { Radio } from '@lifeomic/chroma-react/components/Radio';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Check, X } from '@lifeomic/chromicons';
import TableRowDetail from './TableRowDetail';

export const testIds = {
  tableRow: (linkId: string) => getSurveyItemTestId(linkId, 'row'),
  rowError: (linkId: string) => getSurveyItemTestId(linkId, 'row-error'),
  rowItem: (linkId: string, value: string) =>
    getSurveyItemTestId(linkId, value),
  rowItemIcon: (linkId: string, value: string) =>
    getSurveyItemTestId(linkId, `${value}-icon`),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%', // TODO: this is a placeholder, should be responsive
    },
    questionCell: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
    cellBody: {
      padding: theme.spacing(0.5, 1),
      // Keep radios on the label when a nested detail makes the row taller.
      verticalAlign: 'top',
    },
    // Option columns hug their content so the label column absorbs all
    // remaining width and the options are never pushed out of view.
    optionCell: {
      whiteSpace: 'nowrap',
      width: '1%',
    },
    radioButton: {
      display: 'inline-block',
      // Pad the clickable root so the 21px dot keeps a ~41px touch target
      // even though the row itself is compact.
      padding: theme.spacing(1.25),
      // The Radio renders an empty label container below the dot; hide it so
      // it doesn't add dead height to every row.
      '& > div': {
        display: 'none',
      },
    },
    rowLabel: {
      // Vertically center the single-line label on the radio's touch box.
      marginTop: theme.spacing(1.25),
    },
    errorText: {
      color: theme.palette.error.main,
    },
    // Icon variant (table-check-icons extension): the native radio input is
    // visually hidden but keeps radio semantics; the icon is the visual.
    iconRadio: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(1.25),
      cursor: 'pointer',
      // Circular so the selected chip below reads as a filled token. Harmless
      // when unselected, since there is no background to shape.
      borderRadius: '50%',
      '& input:focus-visible + svg': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: theme.pxToRem(2),
        borderRadius: theme.pxToRem(4),
      },
    },
    // Selected state has to survive a scan down a long checklist. Hue alone was
    // too weak, so the chosen cell also gets a tinted chip and a heavier
    // stroke; the unselected icon keeps its light weight as the affordance.
    iconRadioSelectedYes: {
      backgroundColor: theme.palette.green[100],
    },
    iconRadioSelectedNo: {
      backgroundColor: theme.palette.red[100],
    },
    iconSelected: {
      // Overrides chromicons' strokeWidth={2} presentation attribute.
      strokeWidth: 3,
    },
    iconRadioDisabled: {
      cursor: 'default',
    },
    iconRadioInput: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      margin: 0,
      opacity: 0,
      cursor: 'inherit',
    },
    iconUnselected: {
      color: theme.palette.grey[400],
    },
    iconYesSelected: {
      color: theme.palette.success.main,
    },
    iconNoSelected: {
      // Softened red so a completed checklist doesn't read as a wall of alarms
      color: theme.palette.red[300],
    },
  });

interface FieldComponentOwnProps {
  surveyItem: SurveyItem;
  shouldShowError: boolean;
  error: string;
  choiceOptions: ChoiceOptions;
  isReadOnly: boolean;
  detailItems?: SurveyItem[];
  useCheckIcons?: boolean;
}

type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithStyles<typeof styles>;

class FieldComponent extends React.PureComponent<FieldComponentProps> {
  // The field value may be represented as a complex object in form state, but must be represented as a simple value for the control
  wrapValue = (value: any) => {
    return this.props.choiceOptions.formToResponseValue(value);
  };

  unwrapValue = (responseValue: any) => {
    return this.props.choiceOptions.responseToFormValue(responseValue);
  };

  handleToggle = (event: any) => {
    const { form, field } = this.props;

    const targetValue = event?.target?.value;
    if (!isNil(targetValue)) {
      const unwrappedCurrentValue = this.unwrapValue(this.props.field.value);
      if (isEqual(targetValue, unwrappedCurrentValue)) {
        // toggle off
        form.setFieldValue(field.name, null);
      } else {
        // toggle on
        const wrappedValue = this.wrapValue(targetValue);
        form.setFieldValue(field.name, wrappedValue);
      }
    }
  };

  render() {
    const {
      field: { value },
      surveyItem,
      classes,
      shouldShowError,
      error,
      choiceOptions,
      isReadOnly,
      detailItems,
      useCheckIcons,
    } = this.props;

    const selectValue = this.unwrapValue(value);

    return (
      <TableRow {...getTestProps(testIds.tableRow(surveyItem.link))}>
        <TableCell classes={{ root: classes.cellBody }}>
          <Text
            size="body"
            className={clsx(
              classes.rowLabel,
              shouldShowError && classes.errorText,
            )}
          >
            {surveyItem.label}
          </Text>
          {shouldShowError ? (
            <Text
              size="caption"
              className={shouldShowError ? classes.errorText : undefined}
              {...getTestProps(testIds.rowError(surveyItem.link))}
            >
              {error}
            </Text>
          ) : null}
          {(detailItems || []).map(detailItem => (
            <TableRowDetail key={detailItem.link} surveyItem={detailItem} />
          ))}
        </TableCell>
        {choiceOptions.options.map((option, index) => {
          const optionValue = this.unwrapValue(option.value);
          const checked = optionValue === selectValue;
          // Guard per option value: only exact Yes/No cells render as icons,
          // so mixed tables (e.g. 0-3 scale rows) keep radios untouched.
          const renderAsIcon =
            !!useCheckIcons && (optionValue === 'Yes' || optionValue === 'No');
          return (
            <TableCell
              key={index}
              classes={{ root: clsx(classes.cellBody, classes.optionCell) }}
              size="small"
              align="center"
            >
              {renderAsIcon ? (
                <label
                  className={clsx(
                    classes.iconRadio,
                    isReadOnly && classes.iconRadioDisabled,
                    checked &&
                      (optionValue === 'Yes'
                        ? classes.iconRadioSelectedYes
                        : classes.iconRadioSelectedNo),
                  )}
                  {...getTestProps(
                    testIds.rowItemIcon(surveyItem.link, optionValue),
                  )}
                >
                  <input
                    type="radio"
                    className={classes.iconRadioInput}
                    aria-label={option.display}
                    checked={checked}
                    value={optionValue}
                    onClick={this.handleToggle}
                    readOnly
                    disabled={isReadOnly}
                    {...getTestProps(
                      testIds.rowItem(surveyItem.link, optionValue),
                    )}
                  />
                  {optionValue === 'Yes' ? (
                    <Check
                      aria-hidden
                      className={clsx(
                        checked
                          ? [classes.iconYesSelected, classes.iconSelected]
                          : classes.iconUnselected,
                      )}
                    />
                  ) : (
                    <X
                      aria-hidden
                      className={clsx(
                        checked
                          ? [classes.iconNoSelected, classes.iconSelected]
                          : classes.iconUnselected,
                      )}
                    />
                  )}
                </label>
              ) : (
                <Radio
                  key={`${surveyItem.link}-${index}-${checked}`}
                  label=""
                  aria-label={option.display}
                  className={classes.radioButton}
                  checked={optionValue === selectValue}
                  value={optionValue}
                  onClick={this.handleToggle}
                  disabled={isReadOnly}
                  {...getTestProps(
                    testIds.rowItem(surveyItem.link, optionValue),
                  )}
                />
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }
}

interface ChoiceOwnProps {
  choiceOptions: ChoiceOptions;
  surveyItem: SurveyItem;
  detailItems?: SurveyItem[];
  useCheckIcons?: boolean;
}

export type ChoiceProps = ChoiceOwnProps &
  WithStyles<typeof styles> &
  WithCheckForEnablementInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithSurveyContextInjectedProps &
  WithValidationInjectedProps;

class Choice extends React.PureComponent<ChoiceProps> {
  render() {
    const {
      surveyItem,
      isEnabled,
      shouldShowError,
      error,
      validate,
      choiceOptions,
      classes,
      isReadOnly,
      detailItems,
      useCheckIcons,
    } = this.props;

    if (!isEnabled) return null;

    return (
      <SurveyField
        validate={validate}
        responseAnswerType={surveyItem.responseValueType}
        name={surveyItem.link}
        component={FieldComponent}
        surveyItem={surveyItem}
        isEnabled={isEnabled}
        isReadOnly={isReadOnly}
        error={error}
        shouldShowError={shouldShowError}
        choiceOptions={choiceOptions}
        detailItems={detailItems}
        useCheckIcons={useCheckIcons}
        classes={classes}
      />
    );
  }
}

export default withStyles(styles)(
  withIsReadOnly(
    withCheckForEnablement(withValidation(withSurveyContext(Choice))),
  ),
);
